import React from 'react';
import dayjs from 'dayjs';
import { toCurrency } from '@/helpers/masks/toCurrency';
import PaymentStatus from '@/model/PaymentStatus';
import { States } from '@/helpers/common/states';
import OrderSite from '@/model/OrderSite';
import OrderPayment from '@/model/OrderPayment';
import { DropdonwFlags } from '@/components';
import { ReactComponent as Detail } from '@/assets/images/svg/detail.svg';
import { ReactComponent as Transfer } from '@/assets/images/svg/transfer.svg';
import { ReactComponent as X } from '@/assets/images/svg/x.svg';
import { DataColumn, ShouldShowModalReverse } from '../../types';

interface ClientInfoProps {
  state: States;
  currentPage: OrderSite[];
  order?: OrderSite;
  visible: boolean;
  modal: ShouldShowModalReverse;
  title: string | React.ReactNode;
  onShouldShowModal: (
    value: ShouldShowModalReverse,
    newTitleModal: string | React.ReactNode,
    orderSelected?: OrderSite,
  ) => void;
  onShowReverseOrder: (orderSelected: OrderSite) => void;
  onShowCancelOrder: (orderSelected: OrderSite) => void;
  onToggle: () => void;
}

export const ClientInfo: React.FC<ClientInfoProps> = (props): JSX.Element => {
  const { state, currentPage, onShouldShowModal, onShowReverseOrder, onShowCancelOrder, onToggle } =
    props;
  const mountDataColumnPaymentType = (payments: OrderPayment[]): DataColumn[] => {
    const dataColumnProfiles: DataColumn[] = [];
    if (payments && payments.length > 0) {
      payments.forEach((data, index) => {
        dataColumnProfiles.push({
          id: String(index),
          name: data.transaction,
        });
      });
    }
    return dataColumnProfiles;
  };

  const mountTransaction = (
    payments: OrderPayment[],
    transferOrder?: string,
  ): string | JSX.Element => {
    if (transferOrder) {
      return transferOrder;
    }
    if (payments) {
      if (payments.length === 1) {
        return payments[0].transaction;
      }
      return (
        <DropdonwFlags pointerClass={true} dataColumn={mountDataColumnPaymentType(payments)} />
      );
    }
    return '';
  };

  const getStatus = (paymentStatus: PaymentStatus, transferOrder?: string): string => {
    let value = '';
    if (transferOrder) {
      value = 'Transferido';
    } else if (paymentStatus === PaymentStatus.APPROVED) {
      value = 'Aprovado';
    } else if (paymentStatus === PaymentStatus.DISAPPROVED) {
      value = 'Reprovado';
    } else if (paymentStatus === PaymentStatus.AWAITING_PAYMENT) {
      value = 'Aguardando Pagamento';
    } else if (paymentStatus === PaymentStatus.CANCELED) {
      value = 'Cancelado';
    } else if (paymentStatus === PaymentStatus.CANCELLATION_ANALYSIS) {
      value = 'Aguardando analise cancelamento';
    }

    return value;
  };

  const renderModalContent = (): JSX.Element => {
    if (state === States.loading) {
      return <div className="orders-loading">Carregando pedidos...</div>;
    }
    if (!currentPage || currentPage.length === 0) {
      return <div className="orders-empty">Nenhum pedido encontrado para este cliente.</div>;
    }
    return (
      <div className="orders-cards-list">
        {currentPage.map(data => (
          <div key={data.id} className="order-card">
            <div className="order-card-header">
              <span className="order-id">Pedido: #{data.id}</span>
              <span
                className={`order-status status-${String(data.paymentStatus).toLowerCase()} ${
                  data.transferOrder ? 'status-transferred' : ''
                }`}
              >
                {getStatus(data.paymentStatus, data.transferOrder)}
              </span>
            </div>
            <div className="order-card-body">
              <div className="order-card-info">
                <span className="info-label">Data:</span>
                <span className="info-value">
                  {dayjs(data.orderDate).format('DD/MM/YYYY [às] HH:mm')}
                </span>
              </div>
              <div className="order-card-info">
                <span className="info-label">Valor Total:</span>
                <span className="info-value price-value">{toCurrency(data.totalValue)}</span>
              </div>
              <div className="order-card-info">
                <span className="info-label">Pagamento:</span>
                <span className="info-value">
                  {mountTransaction(data.payments, data.transferOrder)}
                </span>
              </div>
            </div>
            <div className="order-card-actions">
              <button
                type="button"
                className="btn-order-action btn-detail"
                onClick={() => onShouldShowModal(ShouldShowModalReverse.detail, 'Pedido', data)}
                title="Ver Detalhes do Pedido"
              >
                <Detail className="action-icon-svg" /> Detalhes
              </button>
              {!data.cancelDate && (
                <button
                  type="button"
                  className="btn-order-action btn-refund"
                  onClick={() => onShowReverseOrder(data)}
                  title="Estornar Pedido"
                >
                  <Transfer className="action-icon-svg" /> Estornar
                </button>
              )}
              {!data.transferOrder && (
                <button
                  type="button"
                  className="btn-order-action btn-cancel"
                  onClick={() => onShowCancelOrder(data)}
                  title="Cancelar Pedido"
                >
                  <X className="action-icon-svg" /> Cancelar
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <React.Fragment>
      {/* Inline Panel for Orders List */}
      <div className="orders-panel-wrapper">
        <div className="orders-panel-header">
          <strong>Histórico de Pedidos</strong>
          <button
            type="button"
            className="btn-close-panel"
            onClick={onToggle}
            title="Fechar Painel"
          >
            ✕
          </button>
        </div>
        <div
          style={{ padding: '16px', maxHeight: '350px', overflowY: 'auto' }}
          className="orders-modal-body"
        >
          {renderModalContent()}
        </div>
      </div>
    </React.Fragment>
  );
};
